"use client"

import { useCallback } from "react"

import { useIsMutating, useMutationState, useQueryClient, type MutateOptions, type UseQueryOptions } from "@tanstack/react-query"
import { v4 as uuidv4 } from "uuid"

import type {
  AddCartItemRequest,
  CompleteCheckoutRequest,
  CompleteCheckoutResponse,
  LegacyCartResponse,
  MexicoAddress,
  UpdateDeliveryDetailsRequest,
} from "@/generated/api/types.gen"
import { clearReferralApplied, clearStoredReferralCode, isTerminalReferralRejection, rememberReferralRejection } from "@/lib/referrals/attribution"
import { droppedCodeFromError } from "@/lib/referrals/promo-outcome"
import { isMarketplaceError, MarketplaceErrorCode } from "@/lib/utils/marketplace-errors"
import { cartGateway } from "@/platform/api/gateways/cart"
import { checkoutGateway } from "@/platform/api/gateways/checkout"
import { useStateInvalidation } from "@/platform/app-mode/state-context"
import { createConversionId, getStoredAddToCartConversionId } from "@/platform/observability/analytics/conversions"
import { logger } from "@/platform/observability/logger"
import { invalidateDomain, usePrivateKey } from "@/platform/session/private-keys"
import { useGateway } from "@/platform/session/use-api"
import { usePrivateMutation } from "@/platform/session/use-private-mutation"
import { usePrivateQuery } from "@/platform/session/use-private-query"
import { useViewer } from "@/platform/session/use-viewer"
import { privateKey, UNRESOLVED_VIEWER } from "@/platform/session/viewer"

const CART_QUERY_KEY = "cart"

export const useWriteCart = () => {
  const queryClient = useQueryClient()
  const viewer = useViewer()
  const currentKey = usePrivateKey(CART_QUERY_KEY)
  return (cart: LegacyCartResponse) => {
    queryClient.setQueryData(currentKey, cart)
    queryClient.setQueryData(privateKey(viewer ?? UNRESOLVED_VIEWER, CART_QUERY_KEY, cart.cartId), cart)
  }
}

const DISCOUNT_CODE_MUTATION_KEY = ["cart", "discount-code"]

export const useCart = (options?: Omit<UseQueryOptions<LegacyCartResponse | null>, "queryKey" | "queryFn">, allowAnonymous = true) => {
  const carts = useGateway(cartGateway)
  return usePrivateQuery<LegacyCartResponse | null>(
    {
      queryKey: [CART_QUERY_KEY],
      queryFn: () => carts.current(),
      refetchOnWindowFocus: true,
      staleTime: 30000,
      ...options,
    },
    allowAnonymous
  )
}

export const useBag = (enabled = true) => {
  const query = useCart({ enabled }, false)
  const cart = query.data && query.data.state !== "COMPLETED" ? query.data : null
  return { ...query, cart }
}

export const useCartById = (cartId: string, options?: Omit<UseQueryOptions<LegacyCartResponse>, "queryKey" | "queryFn">) => {
  const carts = useGateway(cartGateway)
  return usePrivateQuery<LegacyCartResponse>({
    queryKey: [CART_QUERY_KEY, cartId],
    queryFn: () => carts.byId(cartId),
    refetchOnWindowFocus: true,
    staleTime: 30000,
    ...options,
  })
}

type AddItemVariables = AddCartItemRequest & { conversionId?: string; idempotencyKey: string }
type AddItemInput = AddCartItemRequest & { conversionId?: string }

export const useAddItemToCart = () => {
  const carts = useGateway(cartGateway)
  const writeCart = useWriteCart()
  const invalidate = useStateInvalidation()

  const mutation = usePrivateMutation(
    {
      mutationFn: async ({ conversionId, idempotencyKey, ...request }: AddItemVariables) => {
        const effectiveConversionId = conversionId ?? getStoredAddToCartConversionId() ?? createConversionId()
        return await carts.addItem(request, effectiveConversionId, idempotencyKey)
      },
      retry: 3,
      retryDelay: (attemptIndex) => 200 * 2 ** attemptIndex,
      onSuccess: (cart) => {
        writeCart(cart)
        invalidate("cart")
      },
    },
    true
  )

  return {
    ...mutation,
    mutate: (input: AddItemInput, options?: MutateOptions<LegacyCartResponse, Error, AddItemVariables>) => {
      mutation.mutate({ ...input, idempotencyKey: uuidv4() }, options)
    },
    mutateAsync: (input: AddItemInput, options?: MutateOptions<LegacyCartResponse, Error, AddItemVariables>) => {
      return mutation.mutateAsync({ ...input, idempotencyKey: uuidv4() }, options)
    },
  }
}

export const useRemoveCartItem = (cartId: string) => {
  const queryClient = useQueryClient()
  const carts = useGateway(cartGateway)
  const mutationKey = usePrivateKey(CART_QUERY_KEY, cartId, "remove-item")
  const removing = queryClient.isMutating({ mutationKey, exact: true }) > 0
  const currentKey = usePrivateKey(CART_QUERY_KEY)
  const boundKey = usePrivateKey(CART_QUERY_KEY, cartId)
  const invalidate = useStateInvalidation()

  const mutation = usePrivateMutation(
    {
      mutationKey,
      mutationFn: (lineItemId: string) => carts.removeItem(lineItemId),
      onSuccess: (cart) => {
        queryClient.setQueryData(boundKey, cart)
        queryClient.setQueryData<LegacyCartResponse | null>(currentKey, (current) => (current?.cartId === cartId ? cart : current))
        invalidate("cart")
      },
    },
    true
  )

  return { ...mutation, pendingLineItemId: removing && mutation.isPending ? mutation.variables : null }
}

export const useUpdateShippingAddress = () => {
  const carts = useGateway(cartGateway)
  const writeCart = useWriteCart()

  return usePrivateMutation(
    {
      mutationFn: (address: MexicoAddress) => carts.updateShippingAddress({ address }),
      onSuccess: writeCart,
    },
    true
  )
}

export const useApplyDiscountCode = (onApplied?: (cart: LegacyCartResponse, code: string) => void) => {
  const queryClient = useQueryClient()
  const carts = useGateway(cartGateway)
  const cartKey = usePrivateKey(CART_QUERY_KEY)
  const writeCart = useWriteCart()

  return usePrivateMutation<LegacyCartResponse, Error, string>(
    {
      mutationKey: DISCOUNT_CODE_MUTATION_KEY,
      mutationFn: async (code: string) => {
        await queryClient.cancelQueries({ queryKey: cartKey })
        return carts.applyDiscount(code)
      },
      onSuccess: (cart, code) => {
        writeCart(cart)
        onApplied?.(cart, code)
      },
    },
    true
  )
}

export const useRemoveDiscountCode = () => {
  const queryClient = useQueryClient()
  const carts = useGateway(cartGateway)
  const cartKey = usePrivateKey(CART_QUERY_KEY)
  const writeCart = useWriteCart()

  return usePrivateMutation(
    {
      mutationKey: DISCOUNT_CODE_MUTATION_KEY,
      mutationFn: async (code: string) => {
        await queryClient.cancelQueries({ queryKey: cartKey })
        return carts.removeDiscount(code)
      },
      onSuccess: writeCart,
    },
    true
  )
}

export const useUpdateCredit = () => {
  const queryClient = useQueryClient()
  const carts = useGateway(cartGateway)
  const cartKey = usePrivateKey(CART_QUERY_KEY)
  const writeCart = useWriteCart()

  return usePrivateMutation(
    {
      mutationKey: DISCOUNT_CODE_MUTATION_KEY,
      mutationFn: async ({ action }: { action: "apply" | "remove" }) => {
        await queryClient.cancelQueries({ queryKey: cartKey })
        return action === "apply" ? carts.applyCredit() : carts.removeCredit()
      },
      onSuccess: writeCart,
      onError: () => invalidateDomain(queryClient, CART_QUERY_KEY),
    },
    true
  )
}

export const useIsDiscountCodePending = () => useIsMutating({ mutationKey: DISCOUNT_CODE_MUTATION_KEY }) > 0

export const useDiscountCodePendingNow = () => {
  const queryClient = useQueryClient()
  return useCallback(() => queryClient.isMutating({ mutationKey: DISCOUNT_CODE_MUTATION_KEY }) > 0, [queryClient])
}

export const useUpdateDeliveryDetails = () => {
  const carts = useGateway(cartGateway)
  const writeCart = useWriteCart()

  return usePrivateMutation(
    {
      mutationFn: (request: UpdateDeliveryDetailsRequest) => carts.updateDeliveryDetails(request),
      onSuccess: writeCart,
    },
    true
  )
}

interface CompleteCheckoutVariables {
  request: CompleteCheckoutRequest
  etag: string
  idempotencyKey: string
  conversionId?: string
}

const COMPLETE_CHECKOUT_MUTATION_KEY = ["checkout", "complete"]

export const useCompleteCheckout = () => {
  const viewer = useViewer()
  const queryClient = useQueryClient()
  const checkout = useGateway(checkoutGateway)
  const writeCart = useWriteCart()

  return usePrivateMutation<CompleteCheckoutResponse, Error, CompleteCheckoutVariables>(
    {
      mutationKey: COMPLETE_CHECKOUT_MUTATION_KEY,
      mutationFn: ({ request, etag, idempotencyKey, conversionId }) => checkout.complete(request, idempotencyKey, etag, conversionId),
      onSuccess: (data) => {
        if (data.cart.state === "COMPLETED") logger.info("checkout.completed", { viewer: viewer ?? undefined, cart_id: data.cart.cartId })
        writeCart(data.cart)
      },
      onError: async (error) => {
        const dropped = droppedCodeFromError(error)
        if (dropped) {
          if (isTerminalReferralRejection(dropped.reason)) clearStoredReferralCode(dropped.code)
          clearReferralApplied()
          rememberReferralRejection(dropped)
        }
        if (dropped || isMarketplaceError(error, MarketplaceErrorCode.StoreCreditInsufficient)) {
          await invalidateDomain(queryClient, CART_QUERY_KEY)
        } else if (isMarketplaceError(error, MarketplaceErrorCode.CartStaleState)) {
          void invalidateDomain(queryClient, CART_QUERY_KEY)
        }
      },
    },
    true
  )
}

export const useCompletedCheckout = (cartId: string | null): CompleteCheckoutResponse | undefined =>
  useMutationState({
    filters: { mutationKey: COMPLETE_CHECKOUT_MUTATION_KEY, status: "success" },
    select: (mutation) => mutation.state.data as CompleteCheckoutResponse,
  }).findLast((response) => response.cart.cartId === cartId)
